import React, { useState, useMemo } from 'react'
import { Input, Popover, Tag } from 'antd'
import CategoryTreeSelector from './CategoryTreeSelector'

interface CategoryItem {
  id?: string
  name?: string
  code?: string
  children?: CategoryItem[]
}

interface CategoryTreeInputProps {
  selectedCategories: string[]
  onCategorySelect: (categories: string[]) => void
  categories: CategoryItem[]
  loading: boolean
  hasMore: boolean
  onLoadMore: () => void
  onSearch: (keyword: string) => void
  operator?: string
  disabled?: boolean
}

// Style constants to avoid inline style repetition
const STYLES = {
  selectedContainer: {
    base: {
      border: '1px solid #d9d9d9',
      borderRadius: '2px',
      padding: '8px',
      transition: 'all 0.3s',
      minHeight: '32px',
    },
    enabled: {
      backgroundColor: '#fafafa',
      cursor: 'pointer',
      opacity: 1,
    },
    disabled: {
      backgroundColor: '#f5f5f5',
      cursor: 'not-allowed',
      opacity: 0.6,
    },
    hover: {
      borderColor: '#40a9ff',
      boxShadow: '0 0 0 2px rgba(24, 144, 255, 0.2)',
    },
    default: {
      borderColor: '#d9d9d9',
      boxShadow: 'none',
    },
  },
  countText: {
    marginBottom: '8px',
    color: '#8c8c8c',
    fontSize: '12px',
  },
  tagContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
} as const

const CategoryTreeInput: React.FC<CategoryTreeInputProps> = ({
  selectedCategories,
  onCategorySelect,
  categories,
  loading,
  hasMore,
  onLoadMore,
  onSearch,
  operator,
  disabled = false,
}) => {
  const [visible, setVisible] = useState(false)

  const handleSelect = (categories: string[]) => {
    onCategorySelect(categories)
    setVisible(false)
  }

  // Memoize category name lookup to avoid repeated tree traversals
  const categoryNameMap = useMemo(() => {
    const map = new Map<string, string>()
    const buildMap = (items: CategoryItem[]) => {
      items.forEach(item => {
        if (item.code) {
          map.set(item.code, item.name || item.code)
        }
        if (item.children) {
          buildMap(item.children)
        }
      })
    }
    buildMap(categories)
    return map
  }, [categories])

  const getCategoryName = (code: string): string => {
    return categoryNameMap.get(code) || code
  }

  const getDisplayText = (): string => {
    if (selectedCategories.length === 0) return ''
    
    const categoryCount = selectedCategories.filter(c => c.startsWith('CATEGORY_')).length
    return `${categoryCount} categories selected`
  }

  return (
    <Popover
      content={
        <CategoryTreeSelector
          onSelect={handleSelect}
          selectedCategories={selectedCategories}
          categories={categories}
          loading={loading}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          onSearch={onSearch}
          operator={operator}
        />
      }
      trigger={disabled ? "" : "click"}
      visible={disabled ? false : visible}
      onVisibleChange={disabled ? () => {} : setVisible}
      placement="bottom"
      overlayStyle={{ width: 700 }}
    >
      {selectedCategories.length > 0 ? (
        <div 
          style={{
            ...STYLES.selectedContainer.base,
            ...(disabled ? STYLES.selectedContainer.disabled : STYLES.selectedContainer.enabled),
          }}
          onMouseEnter={(e) => {
            if (!disabled) {
              Object.assign(e.currentTarget.style, STYLES.selectedContainer.hover);
            }
          }}
          onMouseLeave={(e) => {
            if (!disabled) {
              Object.assign(e.currentTarget.style, STYLES.selectedContainer.default);
            }
          }}
        >
          <div style={STYLES.countText}>
            {getDisplayText()}
          </div>
          <div style={STYLES.tagContainer}>
            {selectedCategories.slice(0, 5).map((code) => {
              const categoryName = getCategoryName(code)
              const truncatedName = categoryName.length > 30 
                ? `${categoryName.substring(0, 30)}...` 
                : categoryName
              
              return (
                <Tag 
                  key={code} 
                  color="blue"
                  style={{ margin: 0, fontSize: '11px' }}
                >
                  {truncatedName}
                </Tag>
              )
            })}
            {selectedCategories.length > 5 && (
              <Tag style={{ margin: 0, fontSize: '11px' }}>
                +{selectedCategories.length - 5} more
              </Tag>
            )}
          </div>
        </div>
      ) : (
        <Input 
          placeholder={disabled ? "Please select operator first" : "Select categories..."}
          readOnly
          disabled={disabled}
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        />
      )}
    </Popover>
  )
}

export default CategoryTreeInput